import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Mail, Phone, MapPin, Menu, Instagram } from 'lucide-react';
import { translations, galleryImages, contactInfo, stats } from '../data/mockData';
import '../styles/photography.css';

const PhotographyWebsite = () => {
  const [currentLang, setCurrentLang] = useState('fr');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);

  // Hero images for banner rotation
  const heroImages = [
    '/images/banner_volleyball.jpg',
    '/images/banner_1.jpg',
    '/images/banner_2.jpg',
    '/images/banner_3.jpg',
    '/images/banner_4.jpg'
  ];

  // Auto-rotate hero banner every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const t = translations[currentLang];

  // Show all gallery images (no filters)
  const filteredImages = galleryImages;

  // Open lightbox
  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Navigate lightbox
  const navigateLightbox = (direction) => {
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % filteredImages.length);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
    }
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') navigateLightbox('next');
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [lightboxOpen]);

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="photography-website">
      {/* Navigation */}
      <nav className="nav-photography">
        <div className="nav-content">
          <a href="#home" className="nav-logo" onClick={() => scrollToSection('home')}>
            YAMIL MARQUEZ
          </a>
          
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu />
          </button>

          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a href="#home" className="nav-link" onClick={() => scrollToSection('home')}>
              {t.nav.home}
            </a>
            <a href="#gallery" className="nav-link" onClick={() => scrollToSection('gallery')}>
              {t.nav.gallery}
            </a>
            <a href="#about" className="nav-link" onClick={() => scrollToSection('about')}>
              {t.nav.about}
            </a>
            <a href="#testimonials" className="nav-link" onClick={() => scrollToSection('testimonials')}>
              {t.nav.testimonials}
            </a>
            <a href="#contact" className="nav-link" onClick={() => scrollToSection('contact')}>
              {t.nav.contact}
            </a>
            
            <div className="language-switcher">
              <button 
                className={`lang-btn ${currentLang === 'fr' ? 'active' : ''}`}
                onClick={() => setCurrentLang('fr')}
              >
                FR
              </button>
              <button 
                className={`lang-btn ${currentLang === 'en' ? 'active' : ''}`}
                onClick={() => setCurrentLang('en')}
              >
                EN
              </button>
              <button 
                className={`lang-btn ${currentLang === 'es' ? 'active' : ''}`}
                onClick={() => setCurrentLang('es')}
              >
                ES
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-background">
          {heroImages.map((image, index) => (
            <img 
              key={index}
              src={image} 
              alt={`Sports Photography ${index + 1}`}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: window.innerWidth <= 768 ? 'center 20%' : 'center 30%',
                filter: 'brightness(0.65)',
                opacity: currentHeroImage === index ? 1 : 0,
                transition: 'opacity 1s ease-in-out'
              }}
            />
          ))}
        </div>
        <div className="hero-overlay" style={{
          background: 'linear-gradient(to bottom, rgba(10, 10, 10, 0.2), rgba(10, 10, 10, 0.6))'
        }}></div>
        <div className="hero-content">
          <h1 className="hero-title" style={{
            textShadow: '0 4px 20px rgba(0, 0, 0, 0.8), 0 2px 8px rgba(0, 0, 0, 0.9)'
          }}>
            {t.hero.title}<br />
            <span style={{ color: 'var(--color-accent)' }}>{t.hero.subtitle}</span>
          </h1>
          <p style={{
            textShadow: '0 2px 12px rgba(0, 0, 0, 0.9), 0 1px 4px rgba(0, 0, 0, 1)'
          }}>{t.hero.description}</p>
          <button className="btn-primary" onClick={() => scrollToSection('gallery')}>
            {t.hero.cta}
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding" style={{ background: 'var(--color-dark-secondary)' }}>
        <div className="container-photography">
          <h2 className="section-title" style={{ textAlign: 'center' }}>{t.about.title}</h2>
          <p className="section-subtitle" style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            {t.about.bio}
          </p>
          
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{stats.experience}</div>
              <div className="stat-label">{t.about.experience}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.projects}</div>
              <div className="stat-label">{t.about.projects}</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.sports}</div>
              <div className="stat-label">{t.about.sports}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="section-padding">
        <div className="container-photography">
          <h2 className="section-title" style={{ textAlign: 'center' }}>{t.gallery.title}</h2>
          <p className="section-subtitle" style={{ textAlign: 'center' }}>
            {t.gallery.subtitle}
          </p>


          {/* Gallery Grid */}
          <div className="gallery-grid">
            {filteredImages.map((image, index) => (
              <div 
                key={image.id} 
                className="gallery-item"
                onClick={() => openLightbox(index)}
              >
                <img src={image.url} alt={image.description} />
                <div className="gallery-overlay">
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>{image.sport}</h3>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                    {image.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section-padding" style={{ background: 'var(--color-dark-secondary)' }}>
        <div className="container-photography">
          <h2 className="section-title" style={{ textAlign: 'center' }}>{t.testimonials.title}</h2>
          <p className="section-subtitle" style={{ textAlign: 'center' }}>
            {t.testimonials.subtitle}
          </p>

          <div className="testimonials-grid">
            {t.testimonials.items.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-stars">
                  {'★'.repeat(testimonial.rating)}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-info">
                    <h4>{testimonial.name}</h4>
                    <p>{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding">
        <div className="container-photography">
          <h2 className="section-title" style={{ textAlign: 'center' }}>{t.contact.title}</h2>
          <p className="section-subtitle" style={{ textAlign: 'center' }}>
            {t.contact.subtitle}
          </p>

          {/* Contact Information */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '2rem', 
            maxWidth: '1200px', 
            margin: '0 auto' 
          }}>
            <div className="contact-info-item">
              <h3><Mail size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />{t.contact.info.email}</h3>
              <p><a href={`mailto:${contactInfo.email}`} style={{ color: 'var(--color-text-primary)', textDecoration: 'none' }}>{contactInfo.email}</a></p>
            </div>
            
            <div className="contact-info-item">
              <h3><Phone size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />{t.contact.info.phone}</h3>
              <p><a href={`tel:${contactInfo.phone}`} style={{ color: 'var(--color-text-primary)', textDecoration: 'none' }}>{contactInfo.phone}</a></p>
            </div>
            
            <div className="contact-info-item">
              <h3><MapPin size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />{t.contact.info.location}</h3>
              <p>{contactInfo.location}</p>
            </div>
            
            <div className="contact-info-item">
              <h3><Instagram size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />{t.contact.info.instagram}</h3>
              <p><a href={contactInfo.instagram} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-text-primary)', textDecoration: 'none' }}>{contactInfo.instagramHandle}</a></p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container-photography">
          <div className="footer-content">
            <div className="footer-logo">YAMIL MARQUEZ</div>
            <div className="footer-text">
              © {new Date().getFullYear()} Yamil Marquez. {t.footer.rights}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div className="footer-text">{t.footer.tagline}</div>
              <a 
                href={contactInfo.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ 
                  color: 'var(--color-accent)', 
                  transition: 'color var(--transition-fast)',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-accent-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-accent)'}
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <X />
            </button>
            
            <img 
              src={filteredImages[currentImageIndex].url} 
              alt={filteredImages[currentImageIndex].description}
              className="lightbox-image"
            />
            
            <button 
              className="lightbox-nav prev" 
              onClick={() => navigateLightbox('prev')}
            >
              <ChevronLeft />
            </button>
            
            <button 
              className="lightbox-nav next" 
              onClick={() => navigateLightbox('next')}
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotographyWebsite;
